import administratorPhoto from "@/assets/finlend-admin-photo.jpg";
import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { visibleNavigation, roleLabel } from "@/lib/accountAccess";
import OverdueCustomers from "@/components/OverdueCustomers";
import {
  LayoutDashboard, Users, FileBarChart, Receipt, Settings as SettingsIcon,
  Building2, LogOut, KeyRound, Menu, X, Landmark,
  Trash2, RotateCcw,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const NAV = [
  { to: "/capital-growth", label: "Capital Growth", icon: Landmark, testid: "nav-capital-growth-link" },
  { to: "/statement-of-account", label: "Statement of Account", icon: FileBarChart, testid: "nav-statement-link" },
  { to: "/documents", label: "Documents & Folders", icon: FileBarChart, testid: "nav-documents-link" },
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard-link" },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers-link" },
  { to: "/overdue-customers", label: "Overdue Customers", icon: Users, testid: "nav-overdue-customers-link" },
  { to: "/reports", label: "Reports", icon: FileBarChart, testid: "nav-reports-link" },
  { to: "/loan-monthly-summary", label: "Loan Monthly Summary", icon: FileBarChart, testid: "nav-loan-monthly-summary-link" },
    { to: "/balance-sheet", label: "Balance Sheet", icon: Landmark, testid: "nav-balance-sheet-link" },
    { to: "/emi-paid", label: "EMI Paid", icon: FileBarChart, testid: "nav-emi-paid-link" },
  { to: "/expenses", label: "Expenses", icon: Receipt, testid: "nav-expenses-link" },
  { to: "/business-profile", label: "Business Profile", icon: Building2, testid: "nav-business-link" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings-link" },
];

function ChangePasswordDialog({ open, onOpenChange }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password changed. Please sign in again.");
      localStorage.removeItem("token");
      window.location.assign("/login");
      setCur(""); setNw(""); onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="change-password-dialog">
        <DialogHeader><DialogTitle className="font-heading">Change Password</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Current Password</Label>
            <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="current-password-input" /></div>
          <div><Label>New Password</Label>
            <Input type="password" value={nw} onChange={(e) => setNw(e.target.value)} data-testid="new-password-input" /></div>
        </div>
        <DialogFooter>
          <Button className="bg-maroon hover:bg-maroon-hover" onClick={submit} disabled={loading} data-testid="submit-change-password-button">
            {loading ? "Saving…" : "Update Password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function RecycleBinDialog({ open, onOpenChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState(null);

  const loadRecycleBin = async () => {
    setLoading(true);

    try {
      const res = await api.get("/recycle-bin");
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Failed to load Recycle Bin", e);
      toast.error(
        apiError(e.response?.data?.detail) ||
        "Unable to load Recycle Bin"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadRecycleBin();
    }
  }, [open]);

  const handleOpenChange = (value) => {
    onOpenChange(value);
  };

  const recoverItem = async (item) => {
    const yes = window.confirm(
      `Recover "${item.title || item.item_type}"?`
    );

    if (!yes) return;

    setActionId(item.id);

    try {
      await api.post(`/recycle-bin/${item.id}/restore`);

      toast.success("Record recovered successfully");

      await loadRecycleBin();
    } catch (e) {
      console.error("Recover failed", e);

      toast.error(
        apiError(e.response?.data?.detail) ||
        "Unable to recover record"
      );
    } finally {
      setActionId(null);
    }
  };

  const deletePermanently = async (item) => {
    const yes = window.confirm(
      `Permanently delete "${item.title || item.item_type}"?\n\nThis action cannot be undone.`
    );

    if (!yes) return;

    setActionId(item.id);

    try {
      await api.delete(
        `/recycle-bin/${item.id}/permanent`
      );

      toast.success("Record permanently deleted");

      await loadRecycleBin();
    } catch (e) {
      console.error("Permanent delete failed", e);

      toast.error(
        apiError(e.response?.data?.detail) ||
        "Unable to permanently delete record"
      );
    } finally {
      setActionId(null);
    }
  };

  const formatDeletedDate = (value) => {
    if (!value) return "-";

    try {
      return new Date(value).toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return value;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-6xl max-h-[88vh] overflow-hidden p-0">
        <div className="border-b border-slate-200 px-6 py-4">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-navy flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-maroon" />
              Recycle Bin
            </DialogTitle>
          </DialogHeader>

          <p className="text-sm text-slate-500 mt-1">
            Recently deleted FinLend records can be recovered or permanently deleted.
          </p>
        </div>

        <div className="overflow-auto max-h-[68vh]">
          {loading ? (
            <div className="py-16 text-center text-slate-500">
              Loading deleted records...
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center">
              <Trash2 className="h-12 w-12 mx-auto text-slate-300 mb-3" />

              <div className="font-semibold text-navy">
                Recycle Bin is empty
              </div>

              <div className="text-sm text-slate-500 mt-1">
                Deleted records will appear here.
              </div>
            </div>
          ) : (
            <div className="min-w-[950px]">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr className="border-b border-slate-200 text-left">
                    <th className="px-4 py-3 font-semibold text-navy">
                      Sl No
                    </th>

                    <th className="px-4 py-3 font-semibold text-navy">
                      Type
                    </th>

                    <th className="px-4 py-3 font-semibold text-navy">
                      Details
                    </th>

                    <th className="px-4 py-3 font-semibold text-navy">
                      Reference
                    </th>

                    <th className="px-4 py-3 font-semibold text-navy">
                      Deleted Date
                    </th>

                    <th className="px-4 py-3 font-semibold text-navy">
                      Deleted By
                    </th>

                    <th className="px-4 py-3 font-semibold text-navy text-right">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {items.map((item, index) => (
                    <tr
                      key={item.id}
                      className="border-b border-slate-100 hover:bg-slate-50"
                    >
                      <td className="px-4 py-3 text-slate-600">
                        {index + 1}
                      </td>

                      <td className="px-4 py-3">
                        <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-navy">
                          {item.item_type || "-"}
                        </span>
                      </td>

                      <td className="px-4 py-3 font-medium text-navy">
                        {item.title || "-"}
                      </td>

                      <td className="px-4 py-3 text-slate-600 font-mono text-xs">
                        {item.reference || "-"}
                      </td>

                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                        {formatDeletedDate(item.deleted_at)}
                      </td>

                      <td className="px-4 py-3 text-slate-600">
                        {item.deleted_by || "-"}
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                            disabled={actionId === item.id}
                            onClick={() => recoverItem(item)}
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Recover
                          </Button>

                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-200 text-red-600 hover:bg-red-50"
                            disabled={actionId === item.id}
                            onClick={() => deletePermanently(item)}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete Permanently
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 px-6 py-3 flex justify-end">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


export default function Layout() {
  const { user, logout } = useAuth();
  const { settings, business } = useSettings();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [recycleOpen, setRecycleOpen] = useState(false);

  const rawBizName = "FinLend"; // Shared platform branding for every business.
const bizName =
  typeof rawBizName === "string"
    ? rawBizName
    : rawBizName?.name || rawBizName?.business_name || "FinLend";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#f8fafc]">
      {/* Sidebar */}
      <aside className={`fixed z-40 lg:static inset-y-0 left-0 w-[260px] bg-navy text-white flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-white/10">
          <img
            src="/finlend-logo.png"
            alt="FinLend"
            className="h-11 w-11 rounded-full object-contain bg-white flex-shrink-0"
          />
          <span className="font-heading font-bold text-lg truncate">{bizName}</span>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {visibleNavigation(NAV, user).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-maroon text-white" : "text-slate-300 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              <n.icon className="h-[18px] w-[18px]" />
              {n.label}
            </NavLink>
          ))}

          {user?.platform_admin && (
            <NavLink to="/accounts" onClick={() => setOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-300 hover:bg-white/10">
              <Users className="h-[18px] w-[18px]" /> User Management
            </NavLink>
          )}
          {user?.role !== "collector" && <button
            type="button"
            data-testid="nav-recycle-bin-button"
            onClick={() => {
              setRecycleOpen(true);
              setOpen(false);
            }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-300 hover:bg-white/10 hover:text-white transition-colors"
          >
            <Trash2 className="h-[18px] w-[18px]" />
            Recycle Bin
          </button>}
        </nav>
        <div className="p-3 border-t border-white/10 space-y-1">
          <button onClick={() => setPwOpen(true)} data-testid="open-change-password-button"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-300 hover:bg-white/10 transition-colors">
            <KeyRound className="h-[18px] w-[18px]" /> Change Password
          </button>
          <button onClick={handleLogout} data-testid="logout-button"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-300 hover:bg-maroon transition-colors">
            <LogOut className="h-[18px] w-[18px]" /> Logout
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-8 sticky top-0 z-20">
          <button className="lg:hidden text-navy" onClick={() => setOpen(!open)} data-testid="toggle-sidebar-button">
            {open ? <X /> : <Menu />}
          </button>
          <div className="hidden lg:flex items-center gap-2.5">
            <img
              src="/finlend-logo.png"
              alt="FinLend"
              className="h-9 w-9 rounded-full object-contain"
            />
            <div className="font-heading font-semibold text-navy">{bizName}</div>
          </div>
          <div className="flex items-center gap-3">

            <button
              type="button"
              onClick={() => window.location.reload()}
              title="Refresh"
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-navy shadow-sm hover:bg-slate-50 active:scale-[0.98] transition"
            >
              <RotateCcw className="h-4 w-4" />
              <span className="hidden sm:inline">
                Refresh
              </span>
            </button>

            <div className="text-right leading-tight">
              <div className="text-sm font-semibold text-navy">{typeof (user?.name || user?.username) === "string" ? (user?.name || user?.username) : "Administrator"}</div>
              <div className="text-xs text-slate-400">{roleLabel(user)}</div>
            </div>
            <div className="h-9 w-9 rounded-full bg-maroon text-white flex items-center justify-center font-heading font-bold overflow-hidden">
              {user?.platform_admin && user?.username === "Akashbhavani2985" ? (
                <img src={administratorPhoto} alt="Akash Bhavani" className="h-full w-full object-cover" style={{ objectPosition: "50% 40%" }} />
              ) : String(user?.username || "A")[0].toUpperCase()}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      <ChangePasswordDialog
        open={pwOpen}
        onOpenChange={setPwOpen}
      />

      <RecycleBinDialog
        open={recycleOpen}
        onOpenChange={setRecycleOpen}
      />

    </div>
  );
}
